import { useCallback, useEffect, useRef, useState } from "react";
import type { HomeResponse, NavNode, ShopCard } from "./types";
import { Go, routeForPath, type GoFn, type Route } from "./route";
import { deptRoute } from "./nav";
import { get, rands } from "./shared";
import { Pic } from "./img";
import { IconChevronLeft, IconChevronRight } from "./icons";
import "./HeroCarousel.css";

/**
 * The home page's hero: the weekly carousel (`GET /api/shop/hero`, built by the Worker's
 * shop-hero.ts), or — when no week has been generated yet — the single department hero this page
 * always had. The page's one <h1> lives here too, exactly as it did.
 *
 * The background is an AI-made EMPTY scene; the products on it are the shop's real photos, laid
 * over it as ordinary linked <img> layers. The model never draws anything we sell.
 *
 * Behaviour: auto-advance every 6 s; paused on hover/focus, while the tab is hidden, after the
 * visitor pauses it, and never under prefers-reduced-motion. Scroll-snap track, so a swipe is just
 * a scroll. Region + roledescription="carousel"; the live region is "off" while rotating by
 * itself and "polite" once it is paused or the visitor drives it.
 */

interface HeroSlide {
  position: number;
  theme: string;
  headline: string;
  subline: string;
  ctaLabel: string;
  ctaRoute: string;
  background: string;
  products: ShopCard[];
}
interface HeroResponse { week: string | null; slides: HeroSlide[] }

const INTERVAL_MS = 6000;
/** Fetched once per visit; coming back to Home costs nothing. */
let loadedHero: HeroSlide[] | null = null;
let loadedHome: HomeResponse | null = null;

/** A path the Worker stored (`/d/12-tools`, `/search?q=braai`) → the shop's own Route. */
/** Decided by the router's own table (`routeForPath`), so every route in `shop-routes.ts` works. */
export function routeFromPath(path: string): Route {
  const q = path.indexOf("?");
  const r = routeForPath(q === -1 ? path : path.slice(0, q), q === -1 ? "" : path.slice(q));
  return r.name === "notfound" ? { name: "home" } : r;
}

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function H1() {
  return <div className="hero-h"><div className="wrap"><h1>Everything for the home, the garden and the workshop — at honest prices.</h1></div></div>;
}

export function HeroCarousel({ nav, go }: { nav: NavNode[]; go: GoFn }) {
  const [slides, setSlides] = useState<HeroSlide[] | null>(loadedHero);
  const [home, setHome] = useState<HomeResponse | null>(loadedHome);

  useEffect(() => {
    if (loadedHero) return;
    const ac = new AbortController();
    get<HeroResponse>("/shop/hero", ac.signal)
      .then((d) => { loadedHero = d.slides ?? []; if (!ac.signal.aborted) setSlides(loadedHero); })
      .catch(() => { if (!ac.signal.aborted) setSlides([]); });
    return () => ac.abort();
  }, []);

  /** Only needed for the fallback hero; the same cached endpoint the page's strips use. */
  const needFallback = slides !== null && slides.length === 0;
  useEffect(() => {
    if (!needFallback || loadedHome) return;
    const ac = new AbortController();
    get<HomeResponse>("/shop/home", ac.signal)
      .then((d) => { loadedHome = d; if (!ac.signal.aborted) setHome(d); })
      .catch(() => { /* a plain bar is shown instead */ });
    return () => ac.abort();
  }, [needFallback]);

  if (slides === null) {
    return <section className="hero" aria-label="Featured this week"><div className="hc-slide hc-ph" /><H1 /></section>;
  }
  if (slides.length === 0) return <DepartmentHero nav={nav} go={go} home={home} />;
  return <Carousel slides={slides} go={go} />;
}

function Carousel({ slides, go }: { slides: HeroSlide[]; go: GoFn }) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [tabHidden, setTabHidden] = useState(typeof document !== "undefined" && document.visibilityState === "hidden");
  const [userPaused, setUserPaused] = useState(false);
  const [reduced] = useState(reducedMotion);
  const n = slides.length;

  const showSlide = useCallback((i: number) => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: ((i % n) + n) % n * el.clientWidth, behavior: reducedMotion() ? "auto" : "smooth" });
  }, [n]);

  useEffect(() => {
    const onVis = () => setTabHidden(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const rotating = n > 1 && !reduced && !userPaused && !hovered && !focused && !tabHidden;
  useEffect(() => {
    if (!rotating) return;
    const t = window.setInterval(() => showSlide(active + 1), INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [rotating, active, showSlide]);

  const onScroll = () => {
    const el = trackRef.current;
    if (el && el.clientWidth > 0) setActive(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <section
      className="hero hc"
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured this week"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false); }}
    >
      <div className="hc-stage">
      <div className="hero-track hc-track" ref={trackRef} onScroll={onScroll} aria-live={rotating ? "off" : "polite"}>
        {slides.map((s, i) => (
          <div className="hc-slide" key={s.position} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${n}`}>
            <img
              className="hc-bg"
              src={s.background}
              alt=""
              width={1024}
              height={1024}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "auto"}
              decoding="async"
            />
            <div className="hc-scrim" aria-hidden="true" />
            <div className="wrap hc-body">
              <div className="hc-copy">
                <h2 className="hc-title">{s.headline}</h2>
                <p className="hc-sub">{s.subline}</p>
                <Go to={routeFromPath(s.ctaRoute)} go={go} className="btn btn-action">{s.ctaLabel}</Go>
              </div>
              {s.products.length > 0 && (
                <ul className="hc-prods" aria-label={`In this slide: ${s.headline}`}>
                  {s.products.map((p) => (
                    <li key={p.handle}>
                      <Go to={{ name: "product", handle: p.handle }} go={go} className="hc-prod">
                        {p.image && <span className="hc-prod-img"><Pic src={p.image} alt={p.title} sizes="150px" width={150} height={150} /></span>}
                        <span className="hc-prod-t">{p.title}</span>
                        <span className="hc-prod-p money">{rands(p.priceCents)}</span>
                      </Go>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>

      {n > 1 && (
        <>
          <button type="button" className="hc-arrow prev" aria-label="Previous slide" onClick={() => showSlide(active - 1)}><IconChevronLeft size={22} /></button>
          <button type="button" className="hc-arrow next" aria-label="Next slide" onClick={() => showSlide(active + 1)}><IconChevronRight size={22} /></button>
          <div className="hc-dots" role="group" aria-label="Choose slide">
            {slides.map((s, i) => (
              <button type="button" key={s.position} aria-label={`Show slide ${i + 1}: ${s.headline}`} aria-current={i === active} onClick={() => showSlide(i)} />
            ))}
          </div>
          {!reduced && (
            <button type="button" className="hc-pause" aria-label={userPaused ? "Start the slideshow" : "Pause the slideshow"} aria-pressed={userPaused} onClick={() => setUserPaused((v) => !v)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                {userPaused ? <path d="M8 5v14l11-7z" /> : <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />}
              </svg>
            </button>
          )}
        </>
      )}
      </div>
      <H1 />
    </section>
  );
}

/** What the page showed before the carousel: the biggest departments, one slide each. */
function DepartmentHero({ nav, go, home }: { nav: NavNode[]; go: GoFn; home: HomeResponse | null }) {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [slide, setSlide] = useState(0);
  const imageOf = (id: string) => home?.departmentImages[id] ?? null;
  const top = [...nav].sort((a, b) => b.productCount - a.productCount).slice(0, 6);
  const slides = top.filter((n) => imageOf(n.id)).slice(0, 4);
  const onHeroScroll = () => {
    const el = heroRef.current;
    if (el && el.clientWidth > 0) setSlide(Math.round(el.scrollLeft / el.clientWidth));
  };
  const showSlide = (i: number) => {
    const el = heroRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: reducedMotion() ? "auto" : "smooth" });
  };
  return (
    <section className="hero" aria-label="Featured departments">
      <div className="hero-track" ref={heroRef} onScroll={onHeroScroll}>
        {slides.length === 0 && <div className="hero-slide hero-plain" />}
        {slides.map((n, i) => (
          <div className="hero-slide" key={n.id} aria-roledescription="slide" aria-label={`${i + 1} of ${slides.length}`}>
            <Pic src={imageOf(n.id)!} alt={n.name} sizes="100vw" width={1400} height={560} eager={i === 0} />
            <div className="wrap hero-cap">
              <p className="hero-kicker">{n.productCount} products in stock</p>
              <p className="hero-title">{n.name}</p>
              <Go to={deptRoute(n)} go={go} className="btn btn-action">Shop {n.name}</Go>
            </div>
          </div>
        ))}
      </div>
      <H1 />
      {slides.length > 1 && (
        <div className="hero-dots" role="group" aria-label="Choose slide">
          {slides.map((n, i) => <button type="button" key={n.id} aria-label={`Show ${n.name}`} aria-current={i === slide} onClick={() => showSlide(i)} />)}
        </div>
      )}
    </section>
  );
}
